// @flow

import React, { PureComponent } from 'react';
import qs from 'qs';
import debounce from 'lodash.debounce';
import { Map } from 'immutable';
import { graphql, QueryRenderer } from 'react-relay';
import { i18n, Analytics } from 'shared/utils';
import relayEnvironment from 'shared/gql/relayEnvironment';
import Loader from 'shared/components/common/Loader';
import Page from 'main-app/components/Page';
import PageHeaderList from 'main-app/components/PageHeaderList';
import AddUpdateLotModal from 'main-app/components/AddUpdateLotModal';
import ExportCSVModal from 'main-app/components/ExportCSVModal';
import FilterControls, {
  type FilterValue,
} from 'main-app/components/FilterControls';
import type { RouterHistory } from 'react-router';
import getSortOptions from './getSortOptions';
import getFilterOptions from './getFilterOptions';
import PaginatedLotsContainer from './PaginatedLotsContainer';

type Props = {
  history: RouterHistory,
};

type State = {
  addModalOpen: boolean,
  exportModalOpen: boolean,
  createAnotherLot: boolean,
  search: string,
  graphqlSearchValue: string,
};

const DEFAULT_FILTER = {
  sort: 'CREATED_AT',
  direction: 'DESC',
};

class Lots extends PureComponent<Props, State> {
  constructor(props: Props) {
    super(props);

    this.state = {
      addModalOpen: false,
      exportModalOpen: false,
      createAnotherLot: false,
      search: '',
      graphqlSearchValue: '',
    };

    this.handleDebouncedSearch = debounce(this.handleDebouncedSearch, 500);
  }

  getFilters = (): FilterValue => {
    const { history } = this.props;
    const { search } = history.location;

    if (!search) {
      return DEFAULT_FILTER;
    }

    return {
      ...DEFAULT_FILTER,
      ...qs.parse(search, { ignoreQueryPrefix: true }),
    };
  };

  setFilters = (filters: Object) => {
    const { history } = this.props;

    history.replace(
      '/lots?' +
        qs.stringify(filters, {
          encode: true,
          arrayFormat: 'brackets',
        }),
    );
  };

  filtersDirty = () => {
    const { graphqlSearchValue } = this.state;

    return (
      !Map(this.getFilters()).equals(Map(DEFAULT_FILTER)) || graphqlSearchValue
    );
  };

  compileQueryVariables = () => {
    const filters = this.getFilters();
    const { graphqlSearchValue } = this.state;

    const queryVariables = {
      ...filters,
      search: graphqlSearchValue.trim() || null,
      sortBy: {
        field: filters.sort,
        direction: filters.direction,
      },
    };

    if (queryVariables.createdAt) {
      const [start, end] = queryVariables.createdAt.split(' - ');
      queryVariables.createdAtStart = start;
      queryVariables.createdAtEnd = end;

      delete queryVariables.createdAt;
    }

    delete queryVariables.sort;
    delete queryVariables.direction;

    return queryVariables;
  };

  handleSearchInput = (search: string) => {
    this.setState(
      {
        search,
      },
      this.handleDebouncedSearch,
    );
  };

  handleDebouncedSearch = () => {
    const { search } = this.state;

    this.setState({
      graphqlSearchValue: search,
    });

    Analytics.trackEvent('Search Applied', {
      listName: 'Lots, Lot',
      searchString: search,
    });
  };

  handleCSVExport = async (columns: string[]) => {
    const gqlQueryArgs = {
      ...this.compileQueryVariables(),
    };
    window.open(
      `/api/v1/csv-export-lots?` +
        qs.stringify(
          {
            gqlQueryArgs,
            columns,
          },
          {
            encode: true,
            arrayFormat: 'brackets',
          },
        ),
    );
  };

  handleOpenCreateModal = () => {
    this.setState({
      addModalOpen: true,
    });
  };

  handleOpenExportModal = () => {
    this.setState({
      exportModalOpen: true,
    });
  };

  handleCloseAllModals = () => {
    this.setState({
      addModalOpen: false,
      exportModalOpen: false,
      createAnotherLot: false,
    });
  };

  handleAddModalReopen = () => {
    this.setState(
      {
        addModalOpen: false,
        exportModalOpen: false,
        createAnotherLot: true,
      },
      () => {
        this.setState({
          addModalOpen: true,
        });
      },
    );
  };

  render() {
    const { history } = this.props;
    const {
      search,
      addModalOpen,
      exportModalOpen,
      createAnotherLot,
    } = this.state;

    return (
      <QueryRenderer
        environment={relayEnvironment}
        cacheConfig={{
          force: true,
        }}
        query={graphql`
          query LotsQuery(
            $count: Int!
            $search: String
            $sortBy: LotSort
            $cursor: String
            $lotNumbers: [String!]
            $binNumbers: [String!]
          ) {
            ...PaginatedLotsContainer_lots
              @arguments(
                search: $search
                sortBy: $sortBy
                lotNumbers: $lotNumbers
                binNumbers: $binNumbers
              )
            viewer {
              id
              permissions
            }
          }
        `}
        variables={{
          ...this.compileQueryVariables(),
          count: 30,
          cursor: null,
        }}
        render={({ props }) => (
          <Page>
            <PageHeaderList
              title={i18n.t('Lots')}
              exportButtonLabel={props && i18n.t('Export to CSV')}
              onExportButtonClick={this.handleOpenExportModal}
              createButtonLabel={
                props &&
                props.viewer.permissions.includes('CREATE_LOT') &&
                i18n.t('New Lot')
              }
              onCreateButtonClick={this.handleOpenCreateModal}
              searchPlaceholder={i18n.t('Lot #, Bin #')}
              searchValue={search}
              onSearchChange={this.handleSearchInput}
            />
            <FilterControls
              value={this.getFilters()}
              defaultValue={DEFAULT_FILTER}
              sortOptions={getSortOptions()}
              filterOptions={getFilterOptions()}
              savedFilterType="LOT"
              onFiltersChange={filters => this.setFilters(filters)}
            />
            {!props ? (
              <Loader />
            ) : (
              <PaginatedLotsContainer
                history={history}
                filtersDirty={this.filtersDirty()}
                onCreateNew={this.handleOpenCreateModal}
                lots={props}
                deleteEnabled
                editEnabled={props.viewer.permissions.includes('UPDATE_LOT')}
              />
            )}
            {addModalOpen && (
              <AddUpdateLotModal
                onClose={this.handleCloseAllModals}
                onSuccess={lot => history.push(`/lot/${lot.id}`)}
                onReopen={this.handleAddModalReopen}
                createAnotherLot={createAnotherLot}
                lot={null}
              />
            )}
            {exportModalOpen && (
              <ExportCSVModal
                onClose={this.handleCloseAllModals}
                title={i18n.t('Export Lots to CSV')}
                columnsApiUrl="/api/v1/csv-export-lots/columns"
                onSubmit={this.handleCSVExport}
              />
            )}
          </Page>
        )}
      />
    );
  }
}

export default Lots;
